import API_BASE from './config';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// ── Status badge helper ───────────────────────────────────────────────────────
const StatusBadge = ({ reg }) => {
  if (reg.is_rejected) return (
    <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-red-500/10 border border-red-500/20 text-red-400">Rejected</span>
  );
  if (reg.has_checked_in) return (
    <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">Checked In ✓</span>
  );
  if (reg.is_verified) return (
    <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-sky-500/10 border border-sky-500/20 text-sky-400">Verified</span>
  );
  return (
    <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-amber-500/10 border border-amber-500/20 text-amber-400">Pending Verification</span>
  );
};

// ── Single Ticket Card ────────────────────────────────────────────────────────
const TicketCard = ({ reg }) => {
  const [showQR, setShowQR] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRegisteredAt = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleDownloadQR = () => {
    if (!reg.qr_code_url) return;
    const link = document.createElement('a');
    link.href = reg.qr_code_url;
    link.download = `ticket-${reg.trx_id}.png`;
    link.target = '_blank';
    link.click();
  };

  const statusConfig = reg.is_rejected
    ? {
      label: 'Rejected',
      className: 'bg-red-500/10 border border-red-500/20 text-red-400'
    }
    : reg.has_checked_in
      ? {
        label: 'Checked In',
        className: 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
      }
      : reg.is_verified
        ? {
          label: 'Verified',
          className: 'bg-sky-500/10 border border-sky-500/20 text-sky-400'
        }
        : {
          label: 'Pending',
          className: 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
        };

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/[0.08] bg-gradient-to-br from-[#0b1220] via-[#0a0f18] to-[#05070b] shadow-[0_20px_60px_rgba(0,0,0,0.45)] transition-all duration-300 hover:-translate-y-1 hover:border-sky-500/30">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.12),transparent_28%)]" />
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-sky-400 via-cyan-400 to-sky-500" />

      {reg.event_banner && (
        <div className="relative h-36 overflow-hidden">
          <img
            src={reg.event_banner}
            alt={reg.event_name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          <div className="absolute left-5 bottom-4 right-5 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[9px] font-black text-sky-300 uppercase tracking-[0.35em] mb-1">
                Event Pass
              </p>
              <h3 className="text-lg font-black text-white uppercase tracking-tight truncate">
                {reg.event_name}
              </h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shrink-0 ${statusConfig.className}`}>
              {statusConfig.label}
            </span>
          </div>
        </div>
      )}

      <div className="relative p-5 space-y-5">
        {!reg.event_banner && (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[9px] font-black text-sky-400 uppercase tracking-[0.35em] mb-1">
                Event Pass
              </p>
              <h3 className="text-base font-black text-white uppercase tracking-tight truncate">
                {reg.event_name}
              </h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shrink-0 ${statusConfig.className}`}>
              {statusConfig.label}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-4">
            <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.3em] mb-2">
              Segment
            </p>
            <p className="text-sm font-black text-white uppercase tracking-tight">
              {reg.segment_title}
            </p>

            {reg.team_info?.team_name && (
              <div className="mt-3">
                <p className="text-[8px] font-black text-sky-400 uppercase tracking-[0.25em] mb-1">
                  Team
                </p>
                <p className="text-xs font-bold text-gray-200">
                  {reg.team_info.team_name}
                </p>

                {reg.team_info.members?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {reg.team_info.members.map((m, idx) => (
                      <span
                        key={idx}
                        className="text-[8px] font-bold text-gray-400 bg-white/5 px-2 py-1 rounded-full border border-white/5"
                      >
                        {m.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
              <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.25em] mb-2">
                Date
              </p>
              <p className="text-xs font-bold text-white">
                {reg.event_date ? formatDate(reg.event_date) : 'TBA'}
              </p>
            </div>

            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
              <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.25em] mb-2">
                Venue
              </p>
              <p className="text-xs font-bold text-white truncate">
                {reg.event_venue || 'TBA'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
              <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.25em] mb-2">
                Trx ID
              </p>
              <p className="text-[11px] font-mono font-black text-sky-300 break-all">
                {reg.trx_id}
              </p>
            </div>

            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 text-right">
              <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.25em] mb-2">
                Paid
              </p>
              <p className="text-base font-black text-white">
                {reg.paid_amount} <span className="text-[9px] text-gray-500">BDT</span>
              </p>
            </div>
          </div>
        </div>

        <div className="pt-1">
          <p className="text-[9px] text-gray-600 uppercase tracking-widest">
            Registered {formatRegisteredAt(reg.created_at)}
          </p>
        </div>

        {reg.is_verified && reg.qr_code_url ? (
          <div className="rounded-[1.5rem] border border-sky-500/20 bg-sky-500/[0.04] p-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[8px] font-black text-sky-400 uppercase tracking-[0.3em] mb-1">
                  Entry QR
                </p>
                <p className="text-[11px] text-sky-100/80">
                  Present this at the event gate
                </p>
              </div>

              <button
                onClick={() => setShowQR(v => !v)}
                className="px-4 py-2 rounded-xl bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-sky-400 transition-all"
              >
                {showQR ? 'Hide QR' : 'Show QR'}
              </button>
            </div>

            {showQR && (
              <div className="flex flex-col items-center gap-4 animate-in fade-in duration-300">
                <div className="bg-white p-3 rounded-2xl shadow-[0_0_30px_rgba(56,189,248,0.18)]">
                  <img
                    src={reg.qr_code_url}
                    alt="QR Code"
                    className="w-40 h-40 object-contain"
                  />
                </div>

                <button
                  onClick={handleDownloadQR}
                  className="flex items-center gap-2 text-[10px] font-black text-sky-400 uppercase tracking-widest hover:text-sky-300 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download QR
                </button>
              </div>
            )}
          </div>
        ) : (
          !reg.is_rejected && (
            <div className="rounded-2xl border border-amber-500/10 bg-amber-500/5 px-4 py-4">
              <p className="text-[11px] text-amber-300/90 font-semibold leading-relaxed">
                Your payment is under verification. Once approved, your professional entry pass and QR will appear here.
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ── Main Profile Page ─────────────────────────────────────────────────────────
const ParticipantProfile = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'verified' | 'pending'

  const token = localStorage.getItem('participant_access_token');

  useEffect(() => {
    if (!token) {
      navigate('/participant/login?next=/my-tickets');
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };

    const loadData = async () => {
      try {
        // Profile is optional for page rendering
        try {
          const profileRes = await axios.get(`${API_BASE}/api/participant/profile/`, { headers });
          setProfile(profileRes.data);
        } catch (profileErr) {
          if (profileErr.response?.status === 401) {
            localStorage.removeItem('participant_access_token');
            navigate('/participant/login?next=/my-tickets');
            return;
          }
          console.warn('Profile load failed:', profileErr.response?.data || profileErr.message);
        }

        // Tickets are the important part
        const ticketsRes = await axios.get(`${API_BASE}/api/participant/my-tickets/`, { headers });
        const uniqueTickets = Array.from(
          new Map(ticketsRes.data.map(t => [t.id, t])).values()
        );

        setTickets(uniqueTickets);
        setError('');
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('participant_access_token');
          navigate('/participant/login?next=/my-tickets');
        } else {
          setError('Failed to load tickets.');
          console.error('Ticket load failed:', err.response?.data || err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('participant_access_token');
    localStorage.removeItem('participant_refresh_token');
    localStorage.removeItem('participant_user');
    navigate('/');
  };

  const cleanTickets = Array.from(
    new Map(tickets.map(t => [t.id, t])).values()
  );

  const filteredTickets = cleanTickets.filter(t => {
    if (filter === 'verified') return t.is_verified && !t.is_rejected;
    if (filter === 'pending') return !t.is_verified && !t.is_rejected;
    return true;
  });

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-sky-500 font-black text-xs uppercase tracking-[0.5em] animate-pulse">Loading Tickets...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Background glows */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-5%] left-[-10%] w-[70%] h-[40%] bg-sky-600/5 rounded-full blur-[120px]" />
      </div>

      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest transition-colors">
              Events
            </button>
            <button
              onClick={handleLogout}
              className="text-[10px] font-black text-gray-600 hover:text-red-500 uppercase tracking-widest transition-colors px-3 py-1.5 rounded-lg hover:bg-red-500/10"
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 pt-28 pb-20">
        {/* Profile header */}
        {profile && (
          <div className="mb-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 bg-sky-500/20 border border-sky-500/30 rounded-2xl flex items-center justify-center shrink-0">
              <span className="text-2xl font-black text-sky-400">
                {profile.first_name?.[0]?.toUpperCase() || profile.email?.[0]?.toUpperCase() || 'P'}
              </span>
            </div>
            <div>
              <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-1">My Account</p>
              <h1 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight">
                {profile.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : 'My Tickets'}
              </h1>
              <p className="text-gray-500 text-sm mt-1">{profile.email}</p>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4 text-center">
            <p className="text-2xl font-black text-white">{cleanTickets.length}</p>
            <p className="text-[9px] text-gray-600 uppercase font-bold tracking-widest mt-1">Total</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4 text-center">
            <p className="text-2xl font-black text-sky-400">{cleanTickets.filter(t => t.is_verified).length}</p>
            <p className="text-[9px] text-gray-600 uppercase font-bold tracking-widest mt-1">Verified</p>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-4 text-center">
            <p className="text-2xl font-black text-amber-400">{cleanTickets.filter(t => !t.is_verified && !t.is_rejected).length}</p>
            <p className="text-[9px] text-gray-600 uppercase font-bold tracking-widest mt-1">Pending</p>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'all', label: 'All Tickets' },
            { key: 'verified', label: 'Verified' },
            { key: 'pending', label: 'Pending' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${filter === f.key ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5 hover:text-white'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 mb-6">
            <p className="text-red-400 text-sm font-bold">{error}</p>
          </div>
        )}

        {/* Tickets grid */}
        {filteredTickets.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-white/[0.06] rounded-[2rem]">
            <div className="w-16 h-16 bg-white/[0.03] border border-white/[0.07] rounded-3xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <p className="text-gray-600 font-black uppercase tracking-[0.4em] text-[10px] mb-2">
              {filter === 'all' ? 'No Tickets Yet' : `No ${filter} tickets`}
            </p>
            <p className="text-gray-700 text-xs mb-6">
              {filter === 'all' ? 'Register for an event and your tickets will appear here.' : `You have no ${filter} tickets.`}
            </p>
            {filter === 'all' && (
              <button
                onClick={() => navigate('/')}
                className="bg-sky-500/10 border border-sky-500/20 text-sky-400 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase hover:bg-sky-500 hover:text-black transition-all"
              >
                Browse Events →
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredTickets.map(reg => <TicketCard key={reg.id} reg={reg} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default ParticipantProfile;